import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { TrendingDown, Loader2 } from "lucide-react";

/** Credits + claims per client for a month — where margin is going. */
export default function AdminCreditInsights() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const [y, m] = month.split("-");
    setLoading(true);
    api.get(`/admin/credit-insights?year=${y}&month=${m}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [month]);

  const rows = (data?.clients || []).filter((c) => c.credits_total > 0 || c.claims_filed > 0);

  return (
    <div data-testid="credit-insights" className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-orange-500" /> Credits &amp; claims by client
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Everything credited back in the month — manual goodwill, duplicate-order credits and claim payouts.</p>
        </div>
        <input
          data-testid="credit-insights-month"
          type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm"
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-8 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-6">No credits or claims recorded for {month}.</p>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-x-auto" data-testid="credit-insights-table">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="py-2 px-3">Client</th>
                <th className="py-2 px-3 text-right">Manual</th>
                <th className="py-2 px-3 text-right">Duplicates</th>
                <th className="py-2 px-3 text-right">Claims credited</th>
                <th className="py-2 px-3 text-right">Total credited</th>
                <th className="py-2 px-3 text-right">Claims filed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.account_id} data-testid={`credit-insight-row-${c.account_id}`} className="border-t border-slate-100">
                  <td className="py-2 px-3 font-medium text-slate-800">{c.company_name}</td>
                  <td className="py-2 px-3 text-right">{c.manual ? formatCurrency(c.manual) : "—"}</td>
                  <td className="py-2 px-3 text-right">{c.duplicate ? formatCurrency(c.duplicate) : "—"}</td>
                  <td className="py-2 px-3 text-right">{c.claims_value ? formatCurrency(c.claims_value) : "—"}</td>
                  <td className="py-2 px-3 text-right font-bold text-orange-700">{formatCurrency(c.credits_total + c.claims_value)}</td>
                  <td className="py-2 px-3 text-right text-slate-500">{c.claims_filed || "—"}</td>
                </tr>
              ))}
              <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                <td className="py-2 px-3">All clients</td>
                <td className="py-2 px-3" colSpan={3}></td>
                <td className="py-2 px-3 text-right text-orange-700">
                  {formatCurrency((data?.totals?.credits_total || 0) + (data?.totals?.claims_value || 0))}
                </td>
                <td className="py-2 px-3 text-right">{data?.totals?.claims_filed || 0}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
